import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { ParticleGlobe } from './ParticleGlobe';
import { StationOverlay } from './StationOverlay';
import { ItemPanel } from './ItemPanel';
import { SiteFooter } from './SiteFooter';
import { StaggeredMenu } from '../StaggeredMenu';
import { RadiusMark } from '../RadiusMark';
import { MENU } from '../../data/menu';

// Utility: clamp helper (small enough to inline rather than import lodash).
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/**
 * CinematicExperience
 *
 * Scroll architecture:
 *   - A scroll spacer of (N+1)*100vh drives everything (N=5 stations).
 *   - The stage is `fixed inset-0` so the globe stays parked while the
 *     spacer scrolls; overlays and the SiteFooter live above it.
 *   - Progress 0..1 is computed over the first N*100vh — the last extra
 *     100vh is where the footer reveals.
 */
export const CinematicExperience = () => {
  const N = MENU.length; // 5
  const stateRef = useRef({ progress: 0, activeIndex: 0, arrivalT: 0 });

  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [footerT, setFooterT] = useState(0);
  const [openStationId, setOpenStationId] = useState(null);
  const rafRef = useRef(0);
  const stageRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const denom = N * window.innerHeight;
        const y = window.scrollY;
        const p = clamp(y / denom, 0, 1);
        const ft = clamp((y - denom) / (0.6 * window.innerHeight), 0, 1);
        // Nearest station centre.
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < N; i += 1) {
          const center = (i + 1) / (N + 1);
          const d = Math.abs(p - center);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        const arrivalT = clamp(1 - bestDist / 0.075, 0, 1);
        const effArrivalT = arrivalT * (1 - ft);
        stateRef.current = {
          progress: p,
          activeIndex: best,
          arrivalT: effArrivalT,
        };
        setActiveIndex(best);
        setProgress(p);
        setFooterT(ft);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [N]);

  const goToStation = (idx) => {
    const y = ((idx + 1) / (N + 1)) * N * window.innerHeight;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };
  const goTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const activeStation = MENU[activeIndex];
  const bestDist = Math.abs(progress - (activeIndex + 1) / (N + 1));
  const isArrived = bestDist < 0.05 && footerT < 0.15;
  const arrivalT = clamp(1 - bestDist / 0.075, 0, 1);

  const openStation = useMemo(
    () => MENU.find((m) => m.id === openStationId) || null,
    [openStationId]
  );

  // Stage overall opacity fades as footer scrolls in.
  const stageOpacity = 1 - footerT;

  return (
    <div className="cinematic-scope relative min-h-screen bg-background text-foreground">
      {/* Scroll spacer — drives the whole thing */}
      <div style={{ height: `${(N + 1) * 100}vh` }} aria-hidden />

      {/* Fixed cinematic stage */}
      <div
        ref={stageRef}
        className="fixed inset-0 z-0"
        style={{ opacity: stageOpacity, pointerEvents: stageOpacity < 0.15 ? 'none' : undefined }}
        aria-hidden={footerT > 0.85}
      >
        {/* Radial cyan glow behind everything */}
        <div className="absolute inset-0 radial-fade" />
        {/* Subtle grid */}
        <div className="absolute inset-0 bg-grid opacity-40" style={{ maskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 75%)' }} />
        {/* WebGL layer */}
        <div className="absolute inset-0" data-testid="cinematic-canvas">
          <ParticleGlobe stateRef={stateRef} />
        </div>
        {/* Film grain */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-[0.07]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/></svg>\")",
          }}
        />
        {/* Letterbox bars */}
        <div className="absolute top-0 left-0 right-0 h-[5vh] bg-background pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-[5vh] bg-background pointer-events-none" />
      </div>

      {/* -------- Header (fixed, always on top) -------- */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{ opacity: 1 - footerT * 0.9, pointerEvents: footerT > 0.85 ? 'none' : undefined }}
      >
        {/* StaggeredMenu lives inside so its own z-index dominates on the left */}
        <StaggeredMenu
          items={MENU.map((m) => ({ id: m.id, label: m.label }))}
          onItemClick={(id) => {
            // Direct navigation — no cinematic station intercept. Menu items
            // route straight to the corresponding page.
            const paths = {
              solutions: '/solutions',
              work: '/portfolio',
              company: '/about',
              blog: '/blog',
              resources: '/resources',
              contact: '/contact',
            };
            const p = paths[id];
            if (p) window.location.href = p;
          }}
        />

        {/* Centre wordmark */}
        <button
          type="button"
          onClick={goTop}
          className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-3 text-foreground group"
          data-testid="brand-wordmark"
        >
          <RadiusMark size={34} className="transition-transform group-hover:scale-110" />
          <span className="font-display text-xl tracking-[0.3em] uppercase">Radius</span>
        </button>

        {/* Station rail — fixed to viewport centre so all 5 dots fit at any scroll */}
        <nav
          className="fixed top-1/2 right-6 -translate-y-1/2 hidden lg:flex flex-col gap-4 z-50"
          data-testid="station-rail"
        >
          {MENU.map((m, i) => {
            const active = i === activeIndex && footerT < 0.15;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => goToStation(i)}
                className="group flex items-center gap-3 justify-end"
                data-testid={`station-rail-${m.id}`}
              >
                <span
                  className={`text-[10px] tracking-[0.3em] uppercase transition-colors ${
                    active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                  }`}
                >
                  {m.label}
                </span>
                <span
                  className={`w-2.5 h-2.5 rounded-full transition-all border ${
                    active
                      ? 'bg-primary border-primary scale-125 shadow-glow-red'
                      : 'bg-transparent border-muted-foreground/60 group-hover:border-foreground'
                  }`}
                />
              </button>
            );
          })}
        </nav>
      </header>

      {/* -------- Intro overlay (top of scroll) -------- */}
      <div
        className="fixed inset-0 z-30 pointer-events-none"
        style={{
          // Fade OUT completely before the first station appears (station 1
          // centres at progress = 1/6 ≈ 0.167, so we finish the fade well
          // before that). Fade back IN as scroll passes Contact (station 5).
          opacity: (() => {
            const fadeStart = clamp(1 - progress * 10, 0, 1);
            const fadeEnd = clamp((progress - 0.88) / 0.1, 0, 1) * (1 - footerT);
            return Math.max(fadeStart, fadeEnd);
          })(),
        }}
      >
        {/* Left-aligned hero. Copy is embossed for legibility over the globe
            without a background scrim behind it. */}
        <div className="w-full h-full flex items-start md:items-center px-5 pt-24 md:pt-0 md:pl-14 lg:pl-24">
          <div className="max-w-xl pointer-events-auto">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-primary text-[11px] sm:text-xs uppercase tracking-[0.35em] font-display font-medium mb-5"
            >
              Strategy-Led. Design-Driven. Outcome Focused.
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="font-display font-semibold text-[2.4rem] leading-[1.05] sm:text-6xl lg:text-7xl text-foreground mb-4"
            >
              Connecting The Dots
              <br />
              <span className="text-gradient">That Matter</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="font-sans text-base sm:text-lg text-muted-foreground mb-8"
            >
              Growth Tools &amp; Strategy for Ambitious Businesses.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-wrap gap-3"
            >
              <Button
                variant="hero"
                size="lg"
                onClick={() => setOpenStationId('contact')}
                data-testid="hero-lets-chat-btn"
              >
                Connect
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-muted-foreground">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-float mt-1 text-primary" />
        </div>
      </div>

      {/* -------- Station overlays -------- */}
      <div
        className="fixed inset-0 z-40 pointer-events-none"
        style={{ opacity: 1 - footerT, pointerEvents: footerT > 0.85 ? 'none' : undefined }}
      >
        {activeStation && (
          <StationOverlay
            station={activeStation}
            index={activeIndex}
            arrivalT={arrivalT}
            isArrived={isArrived}
            onEnter={() => setOpenStationId(activeStation.id)}
          />
        )}
      </div>

      {/* Dialog */}
      <ItemPanel
        open={!!openStationId}
        onOpenChange={(v) => setOpenStationId(v ? openStationId : null)}
        station={openStation}
      />

      {/* Footer scrolls over the bottom of the fixed stage */}
      <SiteFooter onOpenStation={(id) => setOpenStationId(id)} />
    </div>
  );
};

export default CinematicExperience;
